/**
 * r16q7 (Live mode, docs/LIVE-MODE-DESIGN.md section 7): the Inbox's Live tick. The server keeps the session; this reads it every 2 s while it's running and keeps its chat the
 * same as the chat open in the browser.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { chatsApi } from './chatsApi';

const RUNNING = ['REQUESTED', 'STARTING', 'READY', 'ENDING'];

/** Pure: is this session still running? */
export const liveRunning = (s) => !!s && RUNNING.includes(s.state);

/** Pure: the words beside the tick. */
export function liveWords(s) {
  if (!s) return '';
  switch (s.state) {
    case 'REQUESTED': return 'Stopping automation on this phone';
    case 'STARTING': return 'Opening Instagram on this account';
    case 'READY': return 'Live';
    case 'ENDING': return 'Ending Live, automation comes back';
    default: break;
  }
  if (s.note) return s.note;
  const why = {
    user: 'Live ended. Automation is back on this phone.',
    idle: `Live ended after ${s.idleMinutes || 10} idle minutes. Automation is back on this phone.`,
    pc_lost: 'The PC stopped answering, so Live ended.',
    no_worker: 'No worker took it: is this PC\'s client running, with this slot\'s phone on the latest worker?',
    start_failed: 'The phone didn\'t get to Instagram in time, so Live ended.',
    worker_failed: 'The worker couldn\'t start Live on this phone.',
    worker_ended: 'Live ended. Automation is back on this phone.',
    switched: 'Live moved to another slot on this phone.',
  };
  return why[s.reason] || 'Live ended.';
}

/**
 * Pure: where a point on the video falls on the phone's screen, as fractions. The picture is fitted inside its box with black bars, so a point on a bar is null.
 * box = the box's {left, top, width, height}; w, h = the picture's own size in pixels.
 */
export function videoPoint(clientX, clientY, box, w, h) {
  if (!box || !(box.width > 0) || !(box.height > 0) || !(w > 0) || !(h > 0)) return null;
  const scale = Math.min(box.width / w, box.height / h);
  const shownW = w * scale;
  const shownH = h * scale;
  const x = (clientX - box.left - (box.width - shownW) / 2) / shownW;
  const y = (clientY - box.top - (box.height - shownH) / 2) / shownH;
  if (x < 0 || x > 1 || y < 0 || y > 1) return null;
  return { x, y };
}

/** Pure: a press that moved less than this many pixels on the video is a tap, else a swipe. */
export const TAP_SLOP_PX = 12;
/** r16q13: held at least this long without moving, it's a long press. */
export const LONG_PRESS_MS = 450;

/** Pure: what a press from a to b on the video means for the phone; null when it starts off the picture. */
export function gesture(a, b, pxMoved, heldMs = 0) {
  if (!a) return null;
  if (!b || pxMoved < TAP_SLOP_PX) return { kind: heldMs >= LONG_PRESS_MS ? 'long' : 'tap', x: a.x, y: a.y };
  // r16q14: with how long the drag took, so a slow drag doesn't turn into a fling on the phone
  return heldMs > 0 ? { kind: 'swipe', x: a.x, y: a.y, x2: b.x, y2: b.y, ms: Math.round(heldMs) } : { kind: 'swipe', x: a.x, y: a.y, x2: b.x, y2: b.y };
}

/** Pure: minutes and seconds for the idle warning. */
export const liveLeftText = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;

/**
 * Pure (r16q10): who leads when the chat open here and the session's chat differ. The person last opened a chat on the phone and this page hasn't caught up: 'follow'. Anything else: this
 * page's chat is the one the person picked: 'focus' sends the phone there. A phone move is followed once, so picking another chat afterwards isn't undone.
 */
export function liveLead({ running, threadId, sessionThread, phoneThread, phoneMovedAt, followedAt }) {
  if (!running || threadId === sessionThread) return null;
  if (phoneMovedAt && phoneMovedAt !== followedAt && phoneThread && phoneThread === sessionThread) return 'follow';
  return threadId ? 'focus' : null;
}

export function useChatLive(slot, threadId, followPhone) {
  const qc = useQueryClient();
  const key = ['chat-live', slot];
  const q = useQuery({
    queryKey: key,
    queryFn: () => chatsApi.liveGet(slot).then((r) => r.data),
    enabled: !!slot,
    retry: false,
    refetchInterval: (query) => (liveRunning(query.state.data && query.state.data.session) ? 2000 : 15000),
  });
  const session = (q.data && q.data.session) || null;
  const enabled = !!(q.data && q.data.enabled);
  const taps = !!(q.data && q.data.taps);
  const running = liveRunning(session);
  const ready = !!session && session.state === 'READY';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const put = useCallback((data) => qc.setQueryData(['chat-live', slot], data), [qc, slot]);

  // the chat the phone follows is the chat open here
  const asked = useRef(null);
  const sessionId = session && session.id;
  const sessionThread = session && session.threadId;
  const phoneThread = session && session.phoneThreadId;
  const phoneMovedAt = session && session.phoneMovedAt;
  const followedAt = useRef(null);
  useEffect(() => {
    if (asked.current) return;
    const lead = liveLead({ running, threadId, sessionThread, phoneThread, phoneMovedAt, followedAt: followedAt.current });
    if (lead === 'follow') {
      followedAt.current = phoneMovedAt;
      if (followPhone && followPhone.current) followPhone.current(sessionThread);
    } else if (lead === 'focus') {
      asked.current = threadId;
      qc.cancelQueries({ queryKey: ['chat-live', slot] });
      chatsApi.liveFocus(sessionId, threadId).then((r) => put(r.data)).catch(() => {}).finally(() => { asked.current = null; });
    }
    // the phone's move is read from the session; followPhone is a ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, threadId, sessionId, sessionThread, phoneThread, phoneMovedAt, put, qc, slot]);

  const start = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await chatsApi.liveStart(slot, threadId);
      put(r.data);
    } catch (e) {
      setError('Live could not start. Try again.');
    } finally {
      setBusy(false);
    }
  }, [slot, threadId, put]);

  const stop = useCallback(async () => {
    if (!sessionId) return;
    setBusy(true);
    setError(null);
    try {
      const r = await chatsApi.liveEnd(sessionId);
      put(r.data);
    } catch (e) {
      setError('Live could not be ended. Try again.');
    } finally {
      setBusy(false);
    }
  }, [sessionId, put]);

  const lastActivity = useRef(0);
  /** "Still here": typing and sending, at most every 30 s; Keep Live passes force. */
  const activity = useCallback((force) => {
    const now = Date.now();
    if (!sessionId || !running || (!force && now - lastActivity.current < 30000)) return;
    lastActivity.current = now;
    chatsApi.liveActivity(sessionId).then((r) => put(r.data)).catch(() => {});
  }, [sessionId, running, put]);

  /** A tap, swipe, Back or "open the chat again" from the phone's video. The worker carries it out on its next tick. */
  const input = useCallback((what) => {
    if (!sessionId || !ready) return Promise.resolve(false);
    return chatsApi.liveInput(sessionId, what).then(() => true).catch(() => false);
  }, [sessionId, ready]);

  return { enabled, taps, session, running, ready, busy, error, start, stop, activity, input };
}
